import type {
  Challenge,
  CreditPackage,
  ParticipantResult,
  PublicProfile,
  Template,
  UserSummary,
  WalletTransaction
} from '@shared/types';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {})
    }
  });
  const payload = await response.json().catch(() => ({})) as { error?: string };
  if (!response.ok) throw new Error(payload.error || 'Não foi possível concluir a solicitação.');
  return payload as T;
}

export const api = {
  register: (input: { name: string; email: string; password: string; publicSlug?: string }) =>
    request<{ data: UserSummary }>('/auth/register', { method: 'POST', body: JSON.stringify(input) }),
  login: (input: { email: string; password: string }) =>
    request<{ data: UserSummary }>('/auth/login', { method: 'POST', body: JSON.stringify(input) }),
  logout: () => request<{ data: { loggedOut: boolean } }>('/auth/logout', { method: 'POST' }),
  me: () => request<{ data: UserSummary }>('/auth/me'),
  updateMe: (input: { name: string; bio: string | null; instagramHandle: string | null; avatarUrl: string | null }) =>
    request<{ data: UserSummary }>('/auth/me', { method: 'PATCH', body: JSON.stringify(input) }),

  templates: () => request<{ data: Template[] }>('/templates'),
  createChallenge: (input: unknown) => request<{ data: { slug: string; creatorToken: string; creditCost: number; freeResponseLimit: number; freeShareLimit: number } }>('/challenges', {
    method: 'POST', body: JSON.stringify(input)
  }),
  challenge: (slug: string) => request<{ data: Challenge }>(`/challenges/${slug}`),
  submitResponse: (slug: string, input: unknown) => request<{ data: { participantId: string; creatorRewardCredits: number } }>(`/challenges/${slug}/responses`, {
    method: 'POST', body: JSON.stringify(input)
  }),
  results: (slug: string, participantId?: string) => request<{
    data: {
      challenge: { title: string; creatorName: string; creatorProfileSlug: string | null; viewCount: number; responseCount: number };
      participant: ParticipantResult | null;
      leaderboard: ParticipantResult[];
    }
  }>(`/challenges/${slug}/results${participantId ? `?participantId=${encodeURIComponent(participantId)}` : ''}`),
  shareStatus: (slug: string, creatorToken?: string) => request<{ data: { paidAccess: boolean; shareCount: number; freeShareLimit: number; remainingFreeShares: number | null; canShare: boolean; rewardPerUniqueResponse: number } }>(`/challenges/${slug}/share-status`, {
    method: 'POST', body: JSON.stringify({ creatorToken })
  }),
  trackShare: (slug: string, source: string, creatorToken?: string) => request<{ data: { paidAccess: boolean; shareCount: number; remainingFreeShares: number | null; rewardPerUniqueResponse: number } }>(`/challenges/${slug}/share`, {
    method: 'POST', body: JSON.stringify({ source, creatorToken })
  }),

  packages: () => request<{ data: CreditPackage[]; payment: { mercadoPagoEnabled: boolean } }>('/credits/packages'),
  wallet: () => request<{ data: { balance: number; unlimitedCredits: boolean; transactions: WalletTransaction[] } }>('/credits/wallet'),
  checkout: (packageSlug: string) => request<{ data: { checkoutUrl: string; externalReference: string } }>('/credits/checkout', {
    method: 'POST', body: JSON.stringify({ packageSlug })
  }),
  order: (externalReference: string) => request<{ data: { status: string; baseCredits: number; bonusCredits: number } }>(`/credits/orders/${externalReference}`),
  syncOrder: (externalReference: string, paymentId: string) => request<{ data: unknown }>(`/credits/orders/${externalReference}/sync`, {
    method: 'POST', body: JSON.stringify({ paymentId })
  }),

  profile: (slug: string) => request<{ data: PublicProfile }>(`/profiles/${slug}`),
  sendInterest: (slug: string, input: unknown) => request<{ data: { created: boolean; message: string } }>(`/profiles/${slug}/interests`, {
    method: 'POST', body: JSON.stringify(input)
  }),
  myInterests: () => request<{ data: Array<{
    id: string; visitorName: string; visitorEmail: string | null; instagramHandle: string | null; message: string | null; createdAt: string;
  }> }>('/profiles/me/interests')
};
